import PermissionsTable from "../../components/ManageEmployeeUsers/PermissionsTable/PermissionsTable";
import NoAuthTable from "../../components/ManageEmployeeUsers/NoAuthTable/NoAuthTable"
import EmployeesInUse from "../../components/ManageEmployeeUsers/EmployeeUsersInUse/EmployeeUsersInUse"
import {ErrorDisplayComponent, ErrorBoundary, PageTitle, Breadcrumbs, DealerMaskMessage, LoadingGears} from '@gplibraries/react/components';
import { useState, useMemo } from 'react';
import { useTokenProvider, useConfig, useDealerInfo, useLogger } from '@gplibraries/react/hooks';
import { useQuery, useQueryClient} from '@tanstack/react-query';
import { fetchAllEmployeeUsers, fetchSisterStoreUsers, fetchOtherDivisionUsers, updateEmployeeUserPermissions, deleteEmployeeUser, createEmployeeUser} from '../../api/employeeUserManagement.js';

export default function ManageEmployeeUsers() {
    const [globalSearchTerm, setGlobalSearchTerm] = useState('');

    const { dealerAccessApiScope, dealerAccessApiHost } = useConfig();
    const getToken = useTokenProvider({ scopes: [dealerAccessApiScope]});

    const { data: dealerInfo } = useDealerInfo();

    const [isCreating, setIsCreating] = useState(false);
    const [deleteingUser, setDeletingUser] = useState(null);
    const [updatingUser, setUpdatingUser] = useState(null);
    const logger = useLogger();
    
    const {
    data: employeeUsers,
    refetch: refetchEmployeeUsers,
    } = useQuery({
    enabled: Boolean(getToken && dealerInfo?.dealer_number),
    queryKey: ['all_dealer_employee_users', dealerInfo?.dealer_number],
    queryFn: () => getToken()
      .then(token => fetchAllEmployeeUsers(dealerAccessApiHost, token?.accessToken, dealerInfo.dealer_number))
      .then(res => res.json())
    });

    const { data: sisterStoreUsers, refetch: refetchSisterStoreUsers } = useQuery({
        enabled: Boolean(getToken && dealerInfo?.dealer_number),
        queryKey: [`sister_store_employee_users`, dealerInfo?.dealer_number],
        queryFn: () => getToken()
          .then(token => fetchSisterStoreUsers(dealerAccessApiHost, token?.accessToken, dealerInfo.dealer_number))
          .then(res => res.json())
    })
    
    const {
    data: otherDivisionUsers,
    refetch: refetchOtherDivisionUsers,
    } = useQuery({
    enabled: Boolean(getToken && dealerInfo?.dealer_number),
    queryKey: ['other_division_employee_users', dealerInfo?.dealer_number],
    queryFn: () => getToken()
      .then(token => fetchOtherDivisionUsers(dealerAccessApiHost, token?.accessToken, dealerInfo.dealer_number))
      .then(res => res.json())
    });

    
    
    
    
    async function handleAuthorizeUser(user) {
        if (!getToken || !dealerInfo?.dealer_number) return;

        
        const nameParts = (user.full_name || "")
        .trim()
        .split(" ")
        .filter(Boolean);

        const firstName = nameParts[0] || "";
        const lastName = nameParts.length > 1
        ? nameParts.slice(1).join(" ")
        : "unknown";


        let sanitizedPermissions = [];

        if (Array.isArray(user.permissions)) {
            // Normalize everything
            sanitizedPermissions = user.permissions
                .filter((entry) => entry?.division)
                .map((entry) => ({
                    division: String(entry.division).padStart(2, "0"),
                    values: Array.isArray(entry.values) ? entry.values.map(String) : [],
                }));
        }
        if (
            Array.isArray(user.permissions) &&
            sanitizedPermissions.some(p => p.division === "01") &&
            !sanitizedPermissions.some(p => p.division === "02")
        ) {
            sanitizedPermissions.push({
                division: "02",
                values: [],
            });
        }
        const payload = {
            username: user.username,
            firstName,
            lastName,
            permissions: sanitizedPermissions, 
        };

        try {
            setIsCreating(true);
            await getToken().then(token => createEmployeeUser(
            dealerAccessApiHost,
              token.accessToken,
            dealerInfo.dealer_number,
            payload
            ));

            await refetchEmployeeUsers();
            await refetchOtherDivisionUsers();
            await refetchSisterStoreUsers();
        } catch (err) {
            console.error('Failed to authorize user', err);
        }
        finally {
            setIsCreating(false);
        }
    }

    // Normalize permissions to always be a flat array of codes
    function normalizePermissions(permissions) {
        if (!permissions) return [];
        // New shape: [{ division: '2', values: ['2','4','6'] }, ...]
        if (Array.isArray(permissions) && permissions.length > 0 && permissions[0]?.values) {
            // Flatten all values arrays
            return [...new Set(
            permissions.flatMap((p) => Array.isArray(p.values) ? p.values : [])
            )].map(String);
        }
        // Old shape: ["1","2","3"]
        if (Array.isArray(permissions)) {
            return permissions.map(String);
        }
        // Object of arrays: { "1": ["1","2","3"], "2": ["5"] }
        if (typeof permissions === 'object') {
            return [...new Set(
            Object.values(permissions).flat().map(String)
            )];
        }
        // Single value
        return [String(permissions)];
    }


    const apiUsers = useMemo(() => {
        const list = Array.isArray(employeeUsers?.data)
            ? employeeUsers.data
            : Array.isArray(employeeUsers)
            ? employeeUsers
            : [];

        return list.map((u) => ({
            employee_user: u.username,
            username: u.username,
            name: u.full_name ?? "",
            email: u.username ?? "",
            permissions: normalizePermissions(u.permissions), // keep flat for display 
            permissionsByDivision: mapPermissionsByDivision(u.permissions),
            rawPermissions: u.permissions ?? null,

        }));
    }, [employeeUsers]);


    const apiSisterUsers = useMemo(() => {
        const list = Array.isArray(sisterStoreUsers?.data)
        ? sisterStoreUsers.data
        : Array.isArray(sisterStoreUsers)
        ? sisterStoreUsers
        : [];

        return list.map((u) => ({
        name: u.full_name ?? "",
        email: u.username ?? "",
        stores: u.stores ?? [],
        }));
    }, [sisterStoreUsers]);

    // Track authorized state for other division users in parent
    const [authorizedOtherDivision, setAuthorizedOtherDivision] = useState({});
    const apiOtherDivisionUsers = useMemo(() => {
        const list = Array.isArray(otherDivisionUsers?.data)
        ? otherDivisionUsers.data
        : Array.isArray(otherDivisionUsers)
        ? otherDivisionUsers
        : [];

        return list.map((u) => ({
            full_name: u.full_name ?? "",
            username: u.username ?? "",
            authorized: !!authorizedOtherDivision[u.username],
            permissions: u.permissions ?? {},
        }));
    }, [otherDivisionUsers, authorizedOtherDivision]);


    const queryClient = useQueryClient();
    
    
    async function handleSavePermissions(updatedUser) {
        if (!getToken || !dealerInfo?.dealer_number) return;
        setUpdatingUser(updatedUser.employee_user);

        try {
            await getToken().then(token => updateEmployeeUserPermissions(
              dealerAccessApiHost,
            token.accessToken,
            dealerInfo.dealer_number,
            updatedUser
            ));

            
            queryClient.setQueryData(
            ['all_dealer_employee_users', dealerInfo?.dealer_number],
            (oldData) => {
                if (Array.isArray(oldData)) {
                return oldData.map((u) =>
                    u.username === updatedUser.employee_user
                    ? { ...u, permissions: updatedUser.permissions.map(String) }
                    : u
                );
                }

                if (Array.isArray(oldData?.data)) {
                return {
                    ...oldData,
                    data: oldData.data.map((u) =>
                    u.username === updatedUser.employee_user
                        ? { ...u, permissions: updatedUser.permissions.map(String) }
                        : u
                    ),
                };
                }

                return oldData;
            }
            );

            // temporarily remove the immediate invalidate here
            // await queryClient.invalidateQueries({ ... });

            await refetchEmployeeUsers();
            await refetchSisterStoreUsers();

            await refetchOtherDivisionUsers();
        } catch (err) {
            console.error('Failed to update user permissions', err);
        } finally {
            setUpdatingUser(null);
        }
    }





    async function handleDeleteUser(username) {
        if (!getToken || !dealerInfo?.dealer_number) return;
        setDeletingUser(username);

        try {
            const response = await getToken().then(token => deleteEmployeeUser(
              dealerAccessApiHost,
            token.accessToken,
            dealerInfo.dealer_number,
            username
            ));

            if (response.ok) {
                await refetchEmployeeUsers();
                await refetchOtherDivisionUsers();
            }

            if (!response.ok) {
                throw new Error(`Failed to delete user: ${response.statusText}`);
            }

        } catch (err) {
            console.error('FAILED STEP:', err);
        } finally {
            setDeletingUser(null);
        }
    }


    function mapPermissionsByDivision(permissions) {
    const result = {
        GP: { enabled: false, codes: {} },
        LP: { enabled: false, codes: {} },
    };

    if (!permissions) return result;


    permissions.forEach((entry) => {
        const division = String(entry.division).padStart(2, "0");

        if (division === "01") {
        result.GP.enabled = true;
        result.GP.codes = Object.fromEntries(
            (entry.values || []).map((code) => [String(code), true])
        );
        }

        if (division === "02") {
        result.LP.enabled = true;
        result.LP.codes = Object.fromEntries(
            (entry.values || []).map((code) => [String(code), true])
        );
        }
    });


    return result;
    }

    
  return (
    <div className = "manage-employee-users">
        <Breadcrumbs/>
        <DealerMaskMessage/>
        <PageTitle>Manage Employee Users</PageTitle>
        <ErrorBoundary
          fallback={<ErrorDisplayComponent message={'Manage Employee Users could not be loaded.'}/>}
          logger={logger}
        >
        
        <div className = "permissions-table-header">
            <p>
            <strong>Note:</strong> A user’s permissions do <strong>not</strong> control email communications.
            To edit the dealer contacts / email communications list, go to{" "}
            <strong className="permissions-note__link">
                Dealer Contacts/Emails
            </strong>{" "}
            to add or update an employee.
            Some areas of the Credit section require an additional password; please contact your Land Pride Credit Manager.
            </p>
        </div>      
            <div className = "meu-content" >
                {isCreating && <LoadingGears />}
                <PermissionsTable 
                    users = {apiUsers} 
                    dealer={dealerInfo} 
                    onSavePermissions={handleSavePermissions} 
                    onDeleteUser = {handleDeleteUser}
                    onInviteUser={handleAuthorizeUser} 
                    searchTerm = {globalSearchTerm} 
                    onSearchChange={setGlobalSearchTerm} 
                    deletingUser={deleteingUser} 
                    updatingUser={updatingUser}
                 />
                <NoAuthTable
                    users={apiOtherDivisionUsers}
                    onAuthorize={user => {
                        handleAuthorizeUser(user);
                        setAuthorizedOtherDivision(prev => ({ ...prev, [user.username]: true }));
                    }}
                    searchTerm = {globalSearchTerm}
                />
                <EmployeesInUse
                users={apiSisterUsers}
                onInvite={handleAuthorizeUser}
                searchTerm={globalSearchTerm}
                dealerInfo={dealerInfo}
                />
            </div>
        </ErrorBoundary>
    </div>
  )
}
